import HelpButton from "@/components/HelpButton/HelpButton";
import NoImpactBanner from "@/components/NoImpactBanner/NoImpactBanner";
import { PageHeader } from "@/components/PageHeader/PageHeader";
import { useCompanyAccess } from "@/hooks/useCompanySettings";
import styles from "@/pages/dashboard/Dashboard.module.scss";
import { Button } from "antd";
import { Link, useNavigate } from "react-router-dom";
import MetricSelector from "../components/MetricSelector/MetricSelector";
import YearPicker from "../components/YearPicker/YearPicker";
import { useCompanyImpactTotals } from "../hooks/useCompanyImpact";
import { useDashboardYear } from "../hooks/useDateRange";
import useImpactMetric from "../hooks/useImpactMetric";
import { useDashboardOrderCounts } from "../hooks/usePoSo";
import CorporateImpactPerScope from "./widgets/CorporateImpactPerScope";
import CorporateImpactPerSegment from "./widgets/CorporateImpactPerSegment";
import CorporateImpactTopContributors from "./widgets/CorporateImpactTopContributors";
import CorporateImpactTotal from "./widgets/CorporateImpactTotal";
import PremiumExplore from "@/components/PremiumFeatureComponents/PremiumExplore";

export default function CorporateDashboard() {
  const metric = useImpactMetric();
  const navigate = useNavigate();
  const access = useCompanyAccess();

  const { data: orderCounts } = useDashboardOrderCounts({});
  const { data: totalImpact } = useCompanyImpactTotals({ metrics: [metric.key] });
  const { year } = useDashboardYear();
  const pageTitle = `Corporate footprint`;
  const pageDescription = "Insight into the total impact of your company.";

  if (access && !access.includes("lca"))
    return (
      <PremiumExplore
        title={pageTitle}
        description={pageDescription}
      />
    );

  return (
    <div>
      <PageHeader
        title={pageTitle}
        description={pageDescription}
        right={
          <>
            <YearPicker />
            <HelpButton url="https://help.root-sustainability.com/en/articles/9899014-company-impact-dashboard" />
          </>
        }
      />
      <MetricSelector
        value={metric.key}
        onChange={(key) => navigate("/dashboard/corporate/" + key)}
      />
      {orderCounts?.total === 0 ? (
        <NoImpactBanner title={`No company impact for ${year}`}>
          <span>Start with uploading purchase and sales orders.</span>
          <span>
            After that, continue with the bill of materials, usage, end of life, facilities and
            transport.
          </span>
          <Link to="/orders/purchase_orders">
            <Button type="primary">Upload orders</Button>
          </Link>
        </NoImpactBanner>
      ) : totalImpact?.company_impact[metric.key] === 0 ? (
        <NoImpactBanner title={`No company impact for ${year}`}>
          <span>What to do?</span>
          <ul>
            <li>
              Upload bill of materials for <Link to="/products">products</Link>.
            </li>
            <li>
              Match the <Link to="/materials">materials</Link> with their impact references.
            </li>
            <li>
              Add utility sources for <Link to="/facilities">facilities</Link>.
            </li>
            <li>
              Upload <Link to="/transport/inbound">transport</Link> data.
            </li>
            <li>
              Provide <Link to="/usage">usage</Link> and <Link to="/eol">end-of-life</Link> data per
              product.
            </li>
          </ul>
        </NoImpactBanner>
      ) : (
        <div className={styles.grid}>
          <div className={styles["two-columns"]}>
            <div>
              <CorporateImpactTotal metric={metric} />
              {metric.key === "carbon_footprint" && <CorporateImpactPerScope />}
              <CorporateImpactPerSegment metric={metric} />
            </div>
            <div>
              <CorporateImpactTopContributors metric={metric} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
